import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import axios from "../../../config/axios";
import useBeaconControl from "../../../hooks/useBeaconControl";
import "./Interact.css";
import InteractTimerTV from "./InteractTimerTV";
import InteractAlertsTV from "./InteractAlertsTV";

// ---- Assets ----
const BASE_SRC = "/static/interactoff.png"; // 1280x800, both rings dark gray = off
const CAST_WELCOME_SRC = "/static/castmember-welcome.png";
const CAST_MAGICAL_SRC = "/static/castmember-magical.png";
const CAST_SAD_SRC     = "/static/castmember-sad.png";
const CAST_POINT_SRC   = "/static/castmember-point.png";
const CAST_ONE_SRC     = "/static/castmember-one.png"; // finger up: "one second"
const FINGER_SRC        = "/static/finger.png";
const FINGER_GREEN_SRC  = "/static/finger-green.png";
const FINGER_PURPLE_SRC = "/static/finger-purple.png";
const FINGER_CYAN_SRC   = "/static/finger-cyan.png";
const CHIME_SRC        = "/static/ball-chime.mp3";

// Module-level so they're created once and reused; loaded lazily on first play.
const CHIME_VOLUME        = 0.4;  // 0..1
let chimeAudio = null;
const playResultSound = () => {
  try {
    if (!chimeAudio) chimeAudio = new Audio(CHIME_SRC);
    chimeAudio.volume = CHIME_VOLUME;
    chimeAudio.currentTime = 0;
    chimeAudio.play().catch((e) => console.log("[InteractTV] sound blocked:", e?.name));
  } catch (e) {
    console.log("[InteractTV] sound failed:", e);
  }
};

// ---- Cast member voice lines (pregenerated, /static). Key → file. ----
const VOICE_VOLUME = 0.9;
const VOICE_SRC = {
  menu:     "/static/voice-menu.mp3",
  hi:       "/static/voice-hi.mp3",
  dvcask:   "/static/voice-dvcask.mp3",
  tap:      "/static/voice-tap.mp3",
  taptv:    "/static/voice-taptv.mp3",
  fingertv: "/static/voice-fingertv.mp3",
  dormant:  "/static/voice-dormant.mp3",
  dormanttv: "/static/voice-dormanttv.mp3",
  herewego: "/static/voice-herewego.mp3",
  finger:   "/static/voice-finger.mp3",
  detected: "/static/voice-detected.mp3",
  keep:     "/static/voice-keep.mp3",
  nudge:    "/static/voice-nudge.mp3",
  magical:  "/static/voice-magical.mp3",
  welcomehome: "/static/voice-welcomehome.mp3",
  pointer:  "/static/voice-pointer.mp3",
  manager:  "/static/voice-manager.mp3",
};
const voiceCache = {};
let currentVoice = null;
const stopVoice = () => {
  try { if (currentVoice) { currentVoice.pause(); currentVoice = null; } } catch {}
};
const playVoice = (key) => {
  try {
    stopVoice();
    if (!VOICE_SRC[key]) return;
    if (!voiceCache[key]) voiceCache[key] = new Audio(VOICE_SRC[key]);
    const a = voiceCache[key];
    a.volume = VOICE_VOLUME;
    a.currentTime = 0;
    currentVoice = a;
    a.play().catch((e) => console.log("[InteractTV] voice blocked:", e?.name));
  } catch (e) {
    console.log("[InteractTV] voice failed:", e);
  }
};

// ---- Colors. Tweak freely. ----
const WHITE = "#ffffff";
const GREEN = "#3dff1a";
const BLUE  = "#5ac8ff";
const PURPLE = "#bf5af2";

// ---- Messages ----
const MSG_TAP        = "Press OK when you're ready to scan!";
const MSG_DORMANT    = "Press OK when you're ready!";
const MSG_HERE_WE_GO = 'Great, get ready to "scan" your finger...';
const MSG_FINGER     = "Use the left and right arrows to align the finger with the scanner";
const MSG_DETECTED   = "Perfect! Hold it right there...";
const MSG_KEEP       = "Keep holding...";
const MSG_NUDGE      = "Hmm...I'm not detecting anything. Move your finger around a bit!";
const MSG_GREEN      = "Have a Magical Day!";
const MSG_DVC_HOME   = "Welcome Home!";
const MSG_POINTER    = "Your pointer finger will work best...";
const MSG_BLUE       = "Let me grab my manager…";
const MSG_DVC_ASK    = "Are you DVC Members?";

// ---- Geometry, measured from interactoff.png (1280x800). Don't touch unless the art changes. ----
const GLOBE = { cx: 452.5, cy: 292, rx: 153, ry: 150, width: 19 };
const SCANNER = { cx: 889, cy: 468.5, rxOut: 124, ryOut: 63.5, rxIn: 113, ryIn: 59, gapStart: 71, gapEnd: 107 };
const ARROW_POS = { x: 889, y: 345 }; // bouncing arrow tip sits above the scanner ring

// ---- Finger hand sprite (finger.png 1157x1360; tip/band measured in Python — don't re-derive) ----
const FINGER_SRC_W = 1157, FINGER_SRC_H = 1360;
const FINGER_TIP = { x: 523, y: 9 };            // fingertip in source px
const FINGER_RENDER_H = 372;                    // stage units; band lands ~y745, wrist clips under the stage
const FINGER_SCALE = FINGER_RENDER_H / FINGER_SRC_H;
const FINGER_RENDER_W = FINGER_SRC_W * FINGER_SCALE;
const FINGER_TARGET = { x: 889, y: 455 };       // tip position when aligned on the pad
const FINGER_MIN_OFF = -240;                    // hand starts here (left of the pad); 0 = aligned
const FINGER_MAX_OFF = 150;
const FINGER_STEP = 30;                         // per arrow press
const FINGER_ALIGN_TOL = 15;                    // step is 30 → only exactly 0 counts as aligned

// TV-only styles: lime focus everywhere, matching the rest of the TV surfaces.
// Focus ring wraps the SCANNER ART (ix-stage-wrap) when the experience has
// the remote; the controls bar and modals carry their own lime highlights.
const IXTV_STYLE = `
    .ixtv-win{outline:none;display:grid;grid-template-columns:1fr 1fr;}
  /* TV-size the Interact menu to match DesignStudioTV step 1 (dstv-mode look) —
     the ds-* classes underneath are display-sized. */
  .ixtv-win .ds-step-tag{font-size:22px;padding:8px 18px;letter-spacing:3px;}
  .ixtv-win .ds-step-title{font-size:44px;}
  .ixtv-win .ds-mode-buttons{display:flex;flex-direction:column;gap:16px;flex:1;justify-content:center;}
  .ixtv-win .ds-mode-btn{height:88px;border-radius:18px;border:4px solid #007bff;background:#fff;
    font-family:"Lato",sans-serif;font-size:40px;font-weight:900;letter-spacing:5px;color:#1a2542;
    cursor:pointer;outline:none;display:flex;align-items:center;justify-content:center;
    transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
  .ixtv-win .ds-mode-btn:focus{background:#007bff;color:#fff;border-color:#05f651;
    box-shadow:0 0 0 5px #05f651,0 0 34px rgba(5,246,81,.55);transform:scale(1.03);}
  .ixtv-win .ds-mode-btn:disabled{opacity:.35;}
  .ixtv-win .ds-btn-big.ds-btn-back{height:84px;border-radius:16px;border:4px solid #d8dce3;background:#fff;
    color:#1a2542;font-family:"Lato",sans-serif;font-size:30px;font-weight:900;letter-spacing:1px;
    flex:0 0 240px;display:flex;align-items:center;justify-content:center;cursor:pointer;outline:none;
    transition:transform .12s ease, box-shadow .12s ease, border-color .12s ease;}
  .ixtv-win .ds-btn-big.ds-btn-back:focus{border-color:#05f651;
    box-shadow:0 0 0 5px #05f651, 0 0 30px rgba(5,246,81,.5);transform:scale(1.03);}
      /* Wrapper owns the bleed on TV: it cancels mbtv-stage's padding (34/40)
     itself, so the tile can size normally inside the flex column instead of
     using .ix-scanner's own negative-margin bleed (which shoved it under
     the bar and overflowed the column). */
  .ixtv-scanwrap{display:flex;flex-direction:column;outline:none;
    height:calc(100% + 34px + 34px);margin:-34px -40px;min-height:0;}
  .ixtv-scanwrap .ixtv-ctlbar{margin:0 0 0;}
  .ixtv-scanwrap .ix-scanner{--ix-bleed:0px;height:auto;flex:1;min-height:0;margin:0;
    border-radius:0 0 24px 24px;}
        .ixtv-ctlbar{flex:0 0 auto;display:flex;justify-content:flex-end;align-items:center;gap:18px;
        padding:12px 40px;margin:-34px -40px 14px;border-radius:0;position:relative;
    background:linear-gradient(180deg,#565e70,#454c5c);border-bottom:1px solid #363c49;
    box-shadow:0 2px 8px rgba(20,30,60,.25);}
      .ixtv-ctlbar .ix-back-btn,
  .ixtv-ctlbar .ix-dvc-pill{position:static;transform:none;font-size:34px;font-weight:900;
    padding:0 44px;height:76px;border-radius:999px;line-height:1;
    display:inline-flex;align-items:center;justify-content:center;text-transform:uppercase;}
  .ixtv-ctlbar .ix-dvc-pill{gap:16px;}
  .ixtv-ctlbar .ix-switch{transform:scale(1.45);transform-origin:center;margin:0 8px;}
  .ixtv-ctlbar .ix-back-btn:focus,
  .ixtv-ctlbar .ix-dvc-pill:focus{outline:none;
    box-shadow:0 0 0 7px #05f651, 0 0 34px rgba(5,246,81,.75);transform:scale(1.06);}
  .ixtv-sink{outline:none;cursor:default;border-radius:28px;flex:1;min-height:0;}
    /* Silhouette outline: interactoff.png has a transparent background (verified),
     so chained offset drop-shadows trace the gold object itself — thick lime
     contour + soft glow. drop-shadow filter, NOT css blur (prod-proven in
     DesignStudioOverlay). */
  .ixtv-sink:focus .ix-img{
    filter:
      drop-shadow(8px 0 0 #05f651) drop-shadow(-8px 0 0 #05f651)
      drop-shadow(0 8px 0 #05f651) drop-shadow(0 -8px 0 #05f651)
      drop-shadow(0 0 16px rgba(5,246,81,.65));
  }
    .ixtv-sink .ix-cast{transform:translateX(200px);}
    .ixtv-sink .ix-stage-wrap{transform:translateX(-70px);pointer-events:none;}
  /* Remote-only surface: the touch hit zones must not be spatial-nav
     focusable (TV browsers focus clickables — that was the stray box on
     the art), and nothing inside the sink shows a UA focus ring. */
  .ixtv-sink .ix-hit{pointer-events:none !important;cursor:default !important;}
  .ixtv-sink img,.ixtv-sink svg,.ixtv-sink button,.ixtv-scanwrap div{outline:none;}
  /* Shared TV modal (takeover + DVC ask) — mbtv-modal look, ported from MagicBandTV. */
  .ixtv-modal-back{position:fixed;inset:0;background:rgba(0,0,0,.62);z-index:10000;
    display:flex;align-items:center;justify-content:center;}
  .ixtv-modal{background:#fff;border-radius:20px;padding:44px 40px 36px;max-width:720px;
    width:calc(100% - 80px);text-align:center;font-family:"Lato",sans-serif;
    box-shadow:0 16px 50px rgba(0,0,0,.5);}
  .ixtv-modal h3{margin:0 0 16px;font-size:40px;color:#1f2937;font-weight:900;}
  .ixtv-modal p{margin:0 0 32px;font-size:26px;color:#4b5563;line-height:1.4;}
  .ixtv-modal-btns{display:flex;gap:18px;justify-content:center;}
  .ixtv-modal-btn{flex:1;padding:20px 28px;border-radius:14px;border:4px solid transparent;
    font-family:"Lato",sans-serif;font-size:28px;font-weight:900;cursor:pointer;outline:none;
    transition:transform .12s ease, box-shadow .12s ease;}
  .ixtv-modal-btn:focus{border-color:#05f651;box-shadow:0 0 0 3px rgba(5,246,81,.4);transform:scale(1.06);}
  .ixtv-modal-no{background:#e5e7eb;color:#374151;border-color:#9ca3af;}
  .ixtv-modal-yes{background:#007bff;color:#fff;}
  .ixtv-ask-on{border-color:#05f651 !important;box-shadow:0 0 0 3px rgba(5,246,81,.4);transform:scale(1.06);}
`;

const annulusPath = (cx, cy, rxOut, ryOut, rxIn, ryIn) =>
  `M ${cx - rxOut} ${cy} a ${rxOut} ${ryOut} 0 1 0 ${rxOut * 2} 0 a ${rxOut} ${ryOut} 0 1 0 ${-rxOut * 2} 0 Z ` +
  `M ${cx - rxIn} ${cy} a ${rxIn} ${ryIn} 0 1 0 ${rxIn * 2} 0 a ${rxIn} ${ryIn} 0 1 0 ${-rxIn * 2} 0 Z`;
const { cx: SX, cy: SY } = SCANNER;
const SCANNER_CORE_PATH = annulusPath(SX, SY, SCANNER.rxOut, SCANNER.ryOut, SCANNER.rxIn, SCANNER.ryIn);
const SCANNER_GLOW_PATH = annulusPath(SX, SY, SCANNER.rxOut + 10, SCANNER.ryOut + 7, SCANNER.rxIn - 9, SCANNER.ryIn - 6);
const SCANNER_HOT_PATH  = annulusPath(SX, SY, SCANNER.rxOut - 2, SCANNER.ryOut - 1.5, SCANNER.rxIn + 2, SCANNER.ryIn + 1);
const gapPoint = (deg) => {
  const t = (deg * Math.PI) / 180;
  return `${SX + 3 * SCANNER.rxOut * Math.cos(t)} ${SY + 3 * SCANNER.ryOut * Math.sin(t)}`;
};
const SCANNER_GAP_CLIP =
  `M 0 0 H 1280 V 800 H 0 Z M ${SX} ${SY} L ${gapPoint(SCANNER.gapStart)} L ${gapPoint(SCANNER.gapEnd)} Z`;

// ---- Screen timing (ms). Tweak freely. ----
const STANDBY_LAP_MS   = 3000;  // standby sweep: one lap
const SCAN_LAP_MS      = 600;   // fast sweep: one lap (runs from CIRCLE all the way through the finger prompt)
const FLASH_CYCLE_MS   = 1200;  // one 100 → 0 → 100 flash
const DISSOLVE_MS      = 600;   // color fade-in / fade-out
const HOLD_REQUIRED_MS = 4000;  // finger must stay down this long → green
const FINGER_WINDOW_MS = 10000; // from the arrow appearing; no held finger by then → blue (a hold in progress is allowed to finish)
const NUDGE_AFTER_MS   = 4000;  // no finger by then → "move your finger around" line
const KEEP_HOLDING_AFTER_MS = 4000; // touch + this long → "Keep holding..." replaces "Perfect!" (runs past hold completion into the resolve wait)
const POINTER_HINT_AFTER_MS = 6000; // tap + this long → "pointer finger" line takes over from "get ready to scan"

// ---- Band timing (ms). Everything is a 2s gap unless noted. ----
const GAP_MS              = 2000;
const STANDBY_FAST_MS     = 2000;  // standby: FAST every N
const ARM_AFTER_WAKEUPS   = 3;     // tap allowed after this many real FAST sends (breathing room so bands are truly awake)
const STANDBY_WAKE_MAX_MS = 60000; // standby FAST loop cap — after this with no tap, CANCEL + dormant (no radio) until tapped
const CIRCLE_REPEAT_MS    = 3000;  // CIRCLE → 3s → CIRCLE again → 3s → CANCEL
const PRELUDE_FAST_COUNT  = 2;     // FASTs after CIRCLE before we're allowed to resolve
const RESULT_HOLD_MS      = 5000;  // SOLID color held on screen this long, then CANCEL
const SOLID_OFF_HOLD_MS   = 5000;  // SOLID_OFF runs this long before standby's wakeups start
const BAND_COLOR_LEAD_MS  = 1500;  // band gets the color command this long BEFORE the screen shows it (field-tuned: 2000 had bands ~1s early)

/* ---- Raw transport — same endpoints as BeaconDemo / MagicBand ---- */
const postShowCmd = (deviceId, fx, vib) =>
  axios
    .post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => console.error("[InteractTV] cmd failed:", fx, err));
const postCancelRaw = (deviceId) =>
  axios
    .post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => console.error("[InteractTV] cancel failed:", err));

// Screen phases:
// standby  → white sweep; band CANCEL → 2s → FAST every 2s; armed after 3 wakeups (or instantly after a cycle); dormant after 60s idle
//            first-ever visit: DVC yes/no question when armed (before the OK prompt)
// welcome  → OK: wait until 2s after the last command → CANCEL → 2s → CIRCLE
// scan     → CIRCLE ×2 on band; globe fast sweep (fast sweep then continues through the finger prompt)
// ready    → CANCEL after circles
// prompt   → first FAST: scanner ring flashing, arrow, finger window open (arrows align the hand)
// green / blue       → band got SOLID color 1.5s ago; screen takes the color, held RESULT_HOLD_MS
// greenOut / blueOut → CANCEL fired; everything fades; SOLID_OFF runs SOLID_OFF_HOLD_MS → standby (already armed)

const GlobeLayers = ({ sweep, lapMs }) => {
  const geo = { cx: GLOBE.cx, cy: GLOBE.cy, rx: GLOBE.rx, ry: GLOBE.ry };
  const sw = sweep ? { pathLength: "100", style: { animationDuration: `${lapMs}ms` } } : {};
  const cls = sweep ? " ix-sweep" : "";
  return (
    <>
      <ellipse className={`ix-glow-outer${cls}`} {...geo} {...sw} strokeWidth={GLOBE.width * 3.6} />
      <ellipse className={`ix-glow${cls}`}       {...geo} {...sw} strokeWidth={GLOBE.width * 2} />
      <ellipse className={`ix-core${cls}`}       {...geo} {...sw} strokeWidth={GLOBE.width} />
      <ellipse className={`ix-hot${cls}`}        {...geo} {...sw} strokeWidth={GLOBE.width * 0.4} />
    </>
  );
};

const ScannerLayers = () => (
  <>
    <path className="ix-glow" d={SCANNER_GLOW_PATH} fillRule="evenodd" clipPath="url(#ixtv-scanner-gap)" />
    <path className="ix-core" d={SCANNER_CORE_PATH} fillRule="evenodd" clipPath="url(#ixtv-scanner-gap)" />
    <path className="ix-hot"  d={SCANNER_HOT_PATH}  fillRule="evenodd" clipPath="url(#ixtv-scanner-gap)" />
  </>
);

const InteractTV = forwardRef(({ onBack, onExitTop, device }, ref) => {
  const [screen, setScreen] = useState("menu"); // "menu" | "scanner"
  const screenRef = useRef("menu");
  useEffect(() => { screenRef.current = screen; }, [screen]);
  const [phase, setPhase] = useState("standby");
  const [armed, setArmed] = useState(false);
  const [dormant, setDormant] = useState(false); // standby wake cap hit; screen alive, radio quiet
  const [fingerDown, setFingerDown] = useState(false);
  const [longHold, setLongHold] = useState(false); // held past KEEP_HOLDING_AFTER_MS
  const [pendingControl, setPendingControl] = useState(false); // take-control modal up
  const [dvc, setDvc] = useState(() => {
    try { return localStorage.getItem("waitsigns_ix_dvc") === "1"; } catch { return false; }
  });
  const [dvcKnown, setDvcKnown] = useState(() => {
    try { return localStorage.getItem("waitsigns_ix_dvc") !== null; } catch { return false; }
  });
  const dvcRef = useRef(dvc);
  useEffect(() => { dvcRef.current = dvc; }, [dvc]);
  const toggleDvc = () => {
    setDvc((v) => {
      const next = !v;
      try { localStorage.setItem("waitsigns_ix_dvc", next ? "1" : "0"); } catch {}
      return next;
    });
    setDvcKnown(true);
  };
  const chooseDvc = (isDvc) => {
    setDvc(isDvc);
    try { localStorage.setItem("waitsigns_ix_dvc", isDvc ? "1" : "0"); } catch {}
    setDvcKnown(true);
  };

  const lastName = device?.subscription?.last_name || null;

  /* ---- Beacon control + gated senders (BeaconDemo pattern) ---- */
  const { isController, hasHolder, holder, claim } = useBeaconControl();
  const isControllerRef = useRef(isController);
  useEffect(() => { isControllerRef.current = isController; }, [isController]);

  const deviceId = device?.magic_beacon_device?._id ?? null;
  const deviceIdRef = useRef(deviceId);
  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);

  const canSend = useCallback(() => !!deviceIdRef.current && !!isControllerRef.current, []);

  const sendCmd = useCallback((fx, vib) => {
    if (!canSend()) {
      console.log(`[InteractTV] SKIPPED fx=${fx} (deviceId=${deviceIdRef.current ? "ok" : "none"}, controller=${isControllerRef.current})`);
      return Promise.resolve();
    }
    console.log(`[InteractTV] send fx=${fx} vib=${vib}`);
    lastCmdAtRef.current = Date.now();
    return postShowCmd(deviceIdRef.current, fx, vib);
  }, [canSend]);
  const sendCancel = useCallback(() => {
    if (!canSend()) {
      console.log(`[InteractTV] SKIPPED CANCEL (deviceId=${deviceIdRef.current ? "ok" : "none"}, controller=${isControllerRef.current})`);
      return Promise.resolve();
    }
    console.log("[InteractTV] send CANCEL");
    lastCmdAtRef.current = Date.now();
    return postCancelRaw(deviceIdRef.current);
  }, [canSend]);

  /* ---- Sequencer ---- */
  const timersRef = useRef([]);
  const loopRef = useRef(null);
  const standbyTimerRef = useRef(null);
  const dormantTimerRef = useRef(null);
  const lastFastAtRef = useRef(0);   // last WAKE_UP_FAST (resolve pacing)
  const lastCmdAtRef  = useRef(0);   // last send of any kind (OK handoff pacing)
  const wakeCountRef = useRef(0);
  const rearmRef = useRef(false); // true when standby follows a completed cycle: skip the greeting, start armed
  const preludeDoneRef = useRef(false);
  const resultRef = useRef(null);
  const windowTimerRef = useRef(null);
  const holdTimerRef = useRef(null);
  const nudgeTimerRef = useRef(null);
  const longHoldTimerRef = useRef(null);
  const [nudge, setNudge] = useState(false);
  const [pointerHint, setPointerHint] = useState(false); // 5s after tap: "pointer finger" line
  const fingerDownRef = useRef(false);     // timer callbacks can't read state
  const windowExpiredRef = useRef(false);  // 10s ran out while a hold was in progress
  const [fingerOff, setFingerOff] = useState(FINGER_MIN_OFF); // hand x offset; 0 = on the pad
  const fingerOffRef = useRef(FINGER_MIN_OFF);
  const [askFocus, setAskFocus] = useState("yes"); // DVC ask: remote-focused answer
  const scanBtnRef = useRef(null);
  const timerBtnRef = useRef(null);
  const alertsBtnRef = useRef(null);
  const backBtnRef = useRef(null);
  // Tab-row Down while on the timer screen: land on the top preset row —
  // not the far-away Back/Start. While running, presets are tabIndex -1,
  // so fall through to the action row (the only focusable thing left).
  const timerBtnFallbackFocus = () => {
    const el =
      document.querySelector(".ixtt-presets button[tabindex='0']") ||
      document.querySelector(".ixtt-actions button[tabindex='0']");
    el?.focus({ preventScroll: true });
  };
  const sinkRef = useRef(null);      // scanner screen key sink
  const backFloatRef = useRef(null); // scanner controls row: Back
  const dvcPillRef = useRef(null);   // scanner controls row: DVC pill
  const pcNoRef = useRef(null);      // take-control modal: No
  const pcYesRef = useRef(null);     // take-control modal: Yes

  const after = useCallback((ms, fn) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);
  const stopLoop = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    clearTimeout(standbyTimerRef.current);
    clearTimeout(dormantTimerRef.current);
  }, []);
  const clearAll = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    stopLoop();
    clearTimeout(windowTimerRef.current);
    clearTimeout(holdTimerRef.current);
    clearTimeout(nudgeTimerRef.current);
    clearTimeout(longHoldTimerRef.current);
  }, [stopLoop]);

  // Unmount = user left via the bottom bar / demo / anywhere outside Interact:
  // CANCEL only — whoever unmounted us (demo, tab change) starts its own
  // choreography with a CANCEL; a trailing SOLID_OFF would stomp it.
  useEffect(() => () => {
    stopVoice();
    try { if (chimeAudio) chimeAudio.pause(); } catch {}
    clearAll();
    if (screenRef.current === "scanner") {
      sendCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-ever visit: ask the DVC question where the OK prompt would appear.
  const asking = screen === "scanner" && phase === "standby" && armed && !dormant && !dvcKnown;

  /* ---- Voice: mirrors the bubble. One line per state change; new line cuts the old. ---- */
  const voiceKey =
    screen === "menu" ? "menu" :
    screen === "timer" ? null :  // timer/alerts load silent; null cuts any playing line
    screen === "alerts" ? null :
    phase === "standby" ? (dormant ? "dormanttv" : asking ? "dvcask" : armed ? "taptv" : "hi") :
    phase === "welcome" || phase === "scan" || phase === "ready" ? (pointerHint ? "pointer" : "herewego") :
    phase === "prompt"
      ? (fingerDown || resultRef.current
          ? (longHold ? "keep" : "detected")
          : nudge ? "nudge" : "fingertv")
      : phase === "green" ? (dvcRef.current ? "welcomehome" : "magical") :
    phase === "blue" ? "manager" : null;

  useEffect(() => {
    if (!voiceKey) { stopVoice(); return; }
    if (voiceKey === "menu") {
      const t = setTimeout(() => playVoice("menu"), 400);
      return () => clearTimeout(t);
    }
    if (voiceKey === "magical" || voiceKey === "welcomehome") {
      const t = setTimeout(() => playVoice(voiceKey), 600); // chime first, then her
      return () => clearTimeout(t);
    }
    playVoice(voiceKey);
  }, [voiceKey]);

  const fireFast = useCallback(() => {
    lastFastAtRef.current = Date.now();
    if (canSend()) wakeCountRef.current += 1;
    sendCmd("WAKE_UP_FAST", "OFF");
    if (wakeCountRef.current >= ARM_AFTER_WAKEUPS) setArmed(true);
  }, [canSend, sendCmd]);

  const resolve = useCallback((kind) => {
    stopLoop();
    const wait = Math.max(0, lastFastAtRef.current + GAP_MS - Date.now());
    console.log(`[InteractTV] resolving ${kind}: CANCEL in ${wait}ms`);
    after(wait, () => {
      sendCancel();
      after(GAP_MS, () => {
        const fx = kind === "green" ? (dvcRef.current ? "SOLID_PURPLE" : "SOLID_GREEN") : "SOLID_CYAN";
        sendCmd(fx, "PULSE");
        after(BAND_COLOR_LEAD_MS, () => {
          setPhase(kind); // screen follows the band by the lead time
          if (kind === "green") playResultSound();
          after(RESULT_HOLD_MS, () => {
            sendCancel();
            setPhase(`${kind}Out`);
            after(GAP_MS, () => {
              sendCmd("SOLID_OFF", "OFF");
              rearmRef.current = true; // bands are warm; standby restarts already tappable
              after(SOLID_OFF_HOLD_MS, () => setPhase("standby"));
            });
          });
        });
      });
    });
  }, [after, stopLoop, sendCmd, sendCancel]);

  const settleResult = useCallback((kind) => {
    if (resultRef.current) return;
    resultRef.current = kind;
    clearTimeout(windowTimerRef.current);
    clearTimeout(holdTimerRef.current);
    setFingerDown(false);
    fingerDownRef.current = false;
    console.log(`[InteractTV] result=${kind} (prelude ${preludeDoneRef.current ? "done" : "still running"})`);
    if (preludeDoneRef.current) resolve(kind);
  }, [resolve]);

  const runPreludeFasts = useCallback((n) => {
    fireFast();
    if (n === 1) setPhase("prompt");
    if (n < PRELUDE_FAST_COUNT) {
      after(GAP_MS, () => runPreludeFasts(n + 1));
      return;
    }
    preludeDoneRef.current = true;
    if (resultRef.current) {
      resolve(resultRef.current);
    } else {
      console.log("[InteractTV] prelude done, no result yet — FAST every 2s until result");
      loopRef.current = setInterval(fireFast, GAP_MS);
    }
  }, [after, fireFast, resolve]);

  /* ---- Standby: CANCEL → 2s → FAST every 2s; arms after 3 wakeups (or instantly post-cycle); dormant after 60s ---- */
  useEffect(() => {
    if (screen !== "scanner" || phase !== "standby" || dormant) return;
    resultRef.current = null;
    preludeDoneRef.current = false;
    if (rearmRef.current) {
      rearmRef.current = false;
      wakeCountRef.current = ARM_AFTER_WAKEUPS; // fresh off a cycle: no greeting, OK ready now
      setArmed(true);
    } else {
      wakeCountRef.current = 0;
      setArmed(false);
    }
    sendCancel();
    standbyTimerRef.current = setTimeout(() => {
      fireFast();
      loopRef.current = setInterval(fireFast, STANDBY_FAST_MS);
    }, GAP_MS);
    dormantTimerRef.current = setTimeout(() => {
      console.log("[InteractTV] standby wake cap hit — CANCEL + dormant until OK");
      stopLoop();
      sendCancel();
      setArmed(false);
      setDormant(true);
    }, STANDBY_WAKE_MAX_MS);
    return stopLoop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, phase, isController, dormant]);

  /* ---- Welcome (OK): wait out the 2s gap from the last command → CANCEL → 2s → CIRCLE ×2 ---- */
  useEffect(() => {
    if (phase !== "welcome") return;
    setPointerHint(false);
    after(POINTER_HINT_AFTER_MS, () => setPointerHint(true));
    const wait = Math.max(0, lastCmdAtRef.current + GAP_MS - Date.now());
    console.log(`[InteractTV] start: CANCEL in ${wait}ms (2s gap from last command)`);
    after(wait, () => sendCancel());
    after(wait + GAP_MS, () => {
      sendCmd("CIRCLE", "OFF");
      setPhase("scan");
      after(CIRCLE_REPEAT_MS, () => {
        sendCmd("CIRCLE", "OFF"); // re-broadcast for bands that missed the first
        after(CIRCLE_REPEAT_MS, () => {
          sendCancel();
          setPhase("ready");
          after(GAP_MS, () => runPreludeFasts(1));
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ---- Prompt: finger window ---- */
  useEffect(() => {
    if (phase !== "prompt") return;
    setPointerHint(false);
    setNudge(false);
    setFingerOff(FINGER_MIN_OFF);
    fingerOffRef.current = FINGER_MIN_OFF;
    windowExpiredRef.current = false;
    windowTimerRef.current = setTimeout(() => {
      if (fingerDownRef.current) {
        // Hold in progress as time ran out — let it finish (green lands in <4s).
        // If they lift instead, handleFingerUp settles blue.
        windowExpiredRef.current = true;
        console.log("[InteractTV] window expired mid-hold — letting the hold finish");
      } else {
        settleResult("blue");
      }
    }, FINGER_WINDOW_MS);
    nudgeTimerRef.current = setTimeout(() => setNudge(true), NUDGE_AFTER_MS);
    return () => {
      clearTimeout(windowTimerRef.current);
      clearTimeout(holdTimerRef.current);
      clearTimeout(nudgeTimerRef.current);
      clearTimeout(longHoldTimerRef.current);
      setFingerDown(false);
      setNudge(false);
      setLongHold(false);
      fingerDownRef.current = false;
      windowExpiredRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const enterScanner = () => {
    setDormant(false);
    setPhase("standby");
    setScreen("scanner");
  };

    // Mirrors the choose-your-color flow: another device holds control → ask first.
  // pendingFor remembers which experience the modal should open on Yes.
  const pendingForRef = useRef("scanner");
  const openScanner = async () => {
    if (isController) { enterScanner(); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "scanner";
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) enterScanner();
    else console.error("[InteractTV] claim failed:", r?.reason);
  };

  // TIMER — same gating; No still enters (timer works lightless).
  const openTimer = async () => {
    if (isController) { setScreen("timer"); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "timer";
      setPendingControl(true);
      return;
    }
        const r = await claim();
    if (!r?.ok) console.error("[InteractTV] timer claim failed:", r?.reason);
    setScreen("timer");
  };

  // ALERTS — scanner rule: nothing but lights, control required to enter.
  // No on the modal does NOT open the screen (unlike the timer).
  const openAlerts = async () => {
    if (isController) { setScreen("alerts"); return; }
    if (hasHolder && holder?.holderDeviceId !== device?._id) {
      pendingForRef.current = "alerts";
      setPendingControl(true);
      return;
    }
    const r = await claim();
    if (r?.ok) setScreen("alerts");
    else console.error("[InteractTV] alerts claim failed:", r?.reason);
  };

  const confirmTakeControl = async () => {
    setPendingControl(false);
    const r = await claim();
        if (pendingForRef.current === "timer") {
      if (!r?.ok) console.error("[InteractTV] take-control claim failed:", r?.reason);
      setScreen("timer"); // timer opens either way
    } else if (pendingForRef.current === "alerts") {
      if (r?.ok) setScreen("alerts");
      else console.error("[InteractTV] take-control claim failed:", r?.reason);
    } else {
      if (r?.ok) enterScanner();
      else console.error("[InteractTV] take-control claim failed:", r?.reason);
    }
  };

  const cancelTakeControl = () => {
    setPendingControl(false);
    // No still lets them into the Timer (lightless); scanner keeps requiring
    // control — it's nothing but lights.
        if (pendingForRef.current === "timer") { setScreen("timer"); return; }
    setTimeout(() => {
      (pendingForRef.current === "alerts" ? alertsBtnRef : scanBtnRef).current?.focus({ preventScroll: true });
    }, 60);
  };

  const leaveScanner = () => {
    stopVoice();
    clearAll();
    sendCancel();
    setTimeout(() => sendCmd("SOLID_OFF", "OFF"), GAP_MS); // untracked on purpose: survives leaving the screen
    setArmed(false);
    setDormant(false);
    setPhase("standby");
    setScreen("menu");
  };

  const handleReaderTap = () => {
    if (asking) return; // answer the DVC question first
    if (phase === "standby" && dormant) {
      setDormant(false); // restarts the standby effect: CANCEL → wakeups → re-arm
      return;
    }
    if (phase !== "standby" || !armed) return;
    stopLoop();
    setPhase("welcome");
  };

  const handleFingerDown = (e) => {
    if (phase !== "prompt" || resultRef.current || windowExpiredRef.current) return;
    e?.currentTarget?.setPointerCapture?.(e.pointerId);
    setFingerDown(true);
    fingerDownRef.current = true;
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => settleResult("green"), HOLD_REQUIRED_MS);
    clearTimeout(longHoldTimerRef.current);
    setLongHold(false);
    longHoldTimerRef.current = setTimeout(() => setLongHold(true), KEEP_HOLDING_AFTER_MS);
  };

  const handleFingerUp = () => {
    clearTimeout(holdTimerRef.current);
    if (!resultRef.current) {
      clearTimeout(longHoldTimerRef.current);
      setLongHold(false);
    }
    setFingerDown(false);
    fingerDownRef.current = false;
    if (windowExpiredRef.current && !resultRef.current) settleResult("blue");
  };

  /* ---- TV: remote slides the hand; crossing onto the pad = finger down,
     sliding off = finger up. Same hold/settle machinery as touch. ---- */
  const moveFinger = (dir) => {
    if (phase !== "prompt" || resultRef.current) return;
    const prev = fingerOffRef.current;
    const next = Math.max(FINGER_MIN_OFF, Math.min(FINGER_MAX_OFF, prev + dir * FINGER_STEP));
    if (next === prev) return;
    fingerOffRef.current = next;
    setFingerOff(next);
    const was = Math.abs(prev) <= FINGER_ALIGN_TOL;
    const is  = Math.abs(next) <= FINGER_ALIGN_TOL;
    if (!was && is) handleFingerDown();
    else if (was && !is) handleFingerUp();
  };

  /* ---- TV: focus plumbing ---- */
  useEffect(() => {
        const grab = () => {
            if (screen === "timer" || screen === "alerts") return; // they own their own focus
      const el = screen === "menu" ? scanBtnRef.current : sinkRef.current;
      el?.focus({ preventScroll: true });
    };
    grab(); // immediately on mount/screen change
    const t1 = setTimeout(grab, 60);   // after the unmounting button's blur settles
    const t2 = setTimeout(() => {
      if (document.activeElement === document.body) grab(); // focus evaporated — take it
    }, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [screen]);

  useEffect(() => { if (asking) setAskFocus("yes"); }, [asking]);

  // Take-control modal: focus Yes on open, trap keys (mbtv-modal pattern).
  useEffect(() => {
    if (pendingControl) {
      const t = setTimeout(() => pcYesRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [pendingControl]);

  useImperativeHandle(ref, () => ({
            focusEntry: () => {
            if (screen === "menu") scanBtnRef.current?.focus();
      else if (screen === "timer") timerBtnFallbackFocus();
      else if (screen === "alerts") document.querySelector(".ixat-edit-btn")?.focus({ preventScroll: true });
      else backFloatRef.current?.focus(); // land in the controls bar, not straight into the box
    },
  }));

  // Key detection widened to BeaconDemo's proven set (LG back=461, Samsung=10009).
  const OKK = (e) => e.key === "Enter" || e.key === " " || e.key === "OK" || e.key === "Select" ||
    e.keyCode === 13 || e.keyCode === 23 || e.keyCode === 66;
  const BACKK = (e) => e.key === "Escape" || e.keyCode === 27 || e.keyCode === 461 || e.keyCode === 10009;

  const handlePcModalKey = (e) => {
    e.stopPropagation();
    const k = e.key;
    if (k === "ArrowLeft") { e.preventDefault(); pcNoRef.current?.focus(); return; }
    if (k === "ArrowRight") { e.preventDefault(); pcYesRef.current?.focus(); return; }
    if (k === "ArrowUp" || k === "ArrowDown") { e.preventDefault(); return; }
    if (OKK(e)) {
      e.preventDefault();
      e.target === pcNoRef.current ? cancelTakeControl() : confirmTakeControl();
      return;
    }
    if (BACKK(e)) { e.preventDefault(); cancelTakeControl(); return; }
  };

        const handleMenuKey = (e) => {
    const onScan = e.target === scanBtnRef.current;
    const onTimer = e.target === timerBtnRef.current;
    const onAlerts = e.target === alertsBtnRef.current;
    const onBk = e.target === backBtnRef.current;
    if (!onScan && !onTimer && !onAlerts && !onBk) return;
    const k = e.key;
    if (k === "ArrowUp") {
      e.preventDefault(); e.stopPropagation();
      if (onScan) onExitTop?.();
      else if (onTimer) scanBtnRef.current?.focus();
      else if (onAlerts) timerBtnRef.current?.focus();
      else alertsBtnRef.current?.focus();
      return;
    }
    if (k === "ArrowDown") {
      if (onBk) return; // bottom-most → bottom bar (native)
      e.preventDefault(); e.stopPropagation();
      if (onScan) timerBtnRef.current?.focus();
      else if (onTimer) alertsBtnRef.current?.focus();
      else backBtnRef.current?.focus();
      return;
    }
    if (k === "ArrowLeft" || k === "ArrowRight") { e.preventDefault(); e.stopPropagation(); return; }
    if (OKK(e)) {
      e.preventDefault(); e.stopPropagation();
      if (onScan) openScanner();
      else if (onTimer) openTimer();
      else if (onAlerts) openAlerts();
      else onBack();
      return;
    }
    if (BACKK(e)) { e.preventDefault(); e.stopPropagation(); onBack(); return; }
  };

  const handleScannerKey = (e) => {
    const k = e.key;
    e.stopPropagation();
    if (BACKK(e)) { e.preventDefault(); leaveScanner(); return; }
    
    // DVC ask is modal: while it's up, ONLY Left/Right/OK work (Back still
    // exits above). Up/Down and everything else are swallowed so nothing
    // behind the popup — ctlbar, bottom bar — can be reached or operated.
    if (asking) {
      e.preventDefault();
      if (k === "ArrowLeft" || k === "ArrowRight") { setAskFocus((f) => (f === "yes" ? "no" : "yes")); return; }
      if (OKK(e)) { chooseDvc(askFocus === "yes"); return; }
      return;
    }

    // ---- Controls row (top strip: DVC pill / Back) ----
    const onCtl = e.target === dvcPillRef.current || e.target === backFloatRef.current;
    if (onCtl) {
      if (k === "ArrowLeft" || k === "ArrowRight") {
        e.preventDefault();
        (e.target === dvcPillRef.current ? backFloatRef.current : dvcPillRef.current)?.focus();
        return;
      }
      if (k === "ArrowUp") { e.preventDefault(); onExitTop?.(); return; } // → tab row
      if (k === "ArrowDown") { e.preventDefault(); sinkRef.current?.focus(); return; } // back down to the experience
      if (OKK(e)) {
        e.preventDefault();
        e.target === dvcPillRef.current ? toggleDvc() : leaveScanner();
        return;
      }
      return;
    }

    // ---- Sink (the experience itself) ----
    if (k === "ArrowLeft" || k === "ArrowRight") {
      e.preventDefault();
      if (asking) setAskFocus((f) => (f === "yes" ? "no" : "yes"));
      else moveFinger(k === "ArrowRight" ? 1 : -1);
      return;
    }
    if (OKK(e)) {
      e.preventDefault();
      if (asking) { chooseDvc(askFocus === "yes"); return; }
      if (phase === "standby") handleReaderTap(); // OK = the tap (arm/dormant rules inside)
      return;
    }
    if (k === "ArrowUp") { e.preventDefault(); backFloatRef.current?.focus(); return; } // → controls row
    if (k === "ArrowDown") return; // → bottom bar (native drop, wizard convention)
  };

  // Menu: focus dropped to nowhere (relatedTarget null = body) means a dead
  // remote — pull it back to BAND SCANNER. Deliberate moves (tabs, bottom
  // bar, menu buttons) carry a real relatedTarget and pass through.
  const handleWindowBlur = (e) => {
    if (screenRef.current !== "menu") return;
    if (e.relatedTarget) return;
    setTimeout(() => {
      if (screenRef.current === "menu" && document.activeElement === document.body) {
        scanBtnRef.current?.focus({ preventScroll: true });
      }
    }, 0);
  };

  // Focus stolen mid-scan (late refocus elsewhere) → take it back; a sink
  // without focus means a dead remote.
  const handleScannerBlur = (e) => {
    if (screenRef.current !== "scanner") return;
    if (e.relatedTarget) return; // deliberate move (tabs / bottom bar / controls) — let it go
    setTimeout(() => {
      if (screenRef.current === "scanner" && document.activeElement === document.body) {
        sinkRef.current?.focus({ preventScroll: true });
      }
    }, 0);
  };
  
  // The TV's spatial nav focuses something invisible between the sink and
  // the bottom bar. Identify it (console) and, when focus arrives on it from
  // anywhere EXCEPT the sink (i.e. climbing up from the bottom bar), redirect
  // to the sink — that makes Up land on the scanner in one press. Coming FROM
  // the sink (heading down) we let it pass so the bottom bar stays reachable.
      const handleScannerFocus = (e) => {
    const onCtl = e.target === dvcPillRef.current || e.target === backFloatRef.current;
    const fromInside = e.currentTarget.contains(e.relatedTarget);
    const fromTabs = typeof e.relatedTarget?.className === "string" &&
      e.relatedTarget.className.includes("mbtv-tab");
    if (onCtl) {
      // Native climb up from the bottom bar (from outside, and not the tab
      // row's deliberate hand-in) should land on the scanner, not jump
      // straight to the controls row.
      if (!fromInside && e.relatedTarget && !fromTabs) {
        sinkRef.current?.focus({ preventScroll: true });
      }
      return;
    }
    if ([sinkRef.current, pcNoRef.current, pcYesRef.current].includes(e.target)) return;
    // Any other stray → sink, unless it's the native drop leaving the sink.
    if (e.relatedTarget === sinkRef.current) return;
    sinkRef.current?.focus({ preventScroll: true });
  };

  /* ===== Menu — built on DesignStudio's own classes so it matches step 1 ===== */
  if (screen === "menu") {
    return (
      <div className="ds-designer ixtv-win" onBlur={handleWindowBlur}>
        <style>{IXTV_STYLE}</style>
        <div className="ds-control-panel" onKeyDown={handleMenuKey}>
          <div className="ds-slide">
            <div className="ds-step-hero">
              <div className="ds-step-tag-row">
                <span className="ds-step-tag">INTERACT</span>
                <div className="ds-step-pills">
                  <span className="ds-step-pill active" />
                  <span className="ds-step-pill" />
                </div>
              </div>
              <h1 className="ds-step-title">Pick An Experience</h1>
            </div>
            <div className="ds-mode-buttons">
              <button className="ds-mode-btn" ref={scanBtnRef} tabIndex={0} onClick={openScanner}>BAND SCANNER</button>
              <button className="ds-mode-btn" ref={timerBtnRef} tabIndex={0} onClick={openTimer}>TIMER</button>
              <button className="ds-mode-btn" ref={alertsBtnRef} tabIndex={0} onClick={openAlerts}>ALERTS</button>
            </div>
            <div className="ds-actions">
              <button className="ds-btn-big ds-btn-back" ref={backBtnRef} tabIndex={0} onClick={onBack}>Back</button>
            </div>
          </div>
        </div>
        <div className="ds-band-stage ix-menu-stage">
          <div className="ix-menu-cast">
            <div className="ix-speech ix-speech-enter">
              {lastName
                ? `Hi, ${lastName} Family! Pick an interactive experience to get started...`
                : "Hi there! Pick an interactive experience to get started..."}
            </div>
            <img className="ix-menu-cast-img" src={CAST_POINT_SRC} alt="" draggable="false" />
          </div>
        </div>

        {pendingControl && hasHolder && holder?.holderDeviceId !== device?._id && (
          <div className="ixtv-modal-back" onKeyDown={handlePcModalKey}>
            <div className="ixtv-modal">
              <h3>Take Control of Your Magic Beacon?</h3>
              <p>
                Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
                currently controlling the beacon. The Band Scanner needs control
                to light your bands. Switch control to this device?
              </p>
              <div className="ixtv-modal-btns">
                <button className="ixtv-modal-btn ixtv-modal-no" ref={pcNoRef} tabIndex={0} onClick={cancelTakeControl}>
                  No
                </button>
                <button className="ixtv-modal-btn ixtv-modal-yes" ref={pcYesRef} tabIndex={0} onClick={confirmTakeControl}>
                  Yes, Switch
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

    /* ===== Timer ===== */
  if (screen === "timer") {
    return (
            <InteractTimerTV
        onBack={() => setScreen("menu")}
        onExitTop={onExitTop}
        device={device}
      />
    );
  }

  /* ===== Alerts ===== */
  if (screen === "alerts") {
    return (
      <InteractAlertsTV
        onBack={() => setScreen("menu")}
        onExitTop={onExitTop}
        device={device}
      />
    );
  }

  /* ===== Band Scanner ===== */
  const isGreen = phase === "green" || phase === "greenOut";
  const isBlue  = phase === "blue"  || phase === "blueOut";
  const isOut   = phase === "greenOut" || phase === "blueOut";
  const isColor = phase === "green" || phase === "blue";
  const ringColor = isGreen ? (dvc ? PURPLE : GREEN) : isBlue ? BLUE : WHITE;
  const colorTransition = isColor || isOut ? `opacity ${DISSOLVE_MS}ms ease` : "none";

  // Globe never goes solid white: it sweeps in every non-result phase.
  const globeSweep = !isColor && !isOut;
  const globeFastLap = phase === "scan" || phase === "ready" || phase === "prompt";
  const globeStyle = { color: ringColor, opacity: isColor ? 1 : 0, transition: colorTransition };

  const scannerFlash = phase === "prompt";
  const scannerStyle = {
    color: ringColor,
    opacity: isColor ? 1 : 0,
    transition: colorTransition,
    animationDuration: `${FLASH_CYCLE_MS}ms`,
  };

  // Pulsing target in the middle of the globe while waiting for OK.
  const showTapPulse = phase === "standby" && armed && !dormant && !asking;

  // Hand sprite: appears at the finger prompt, stays through the result so
  // the wrist band can light with the rings.
  const showHand = phase === "prompt" || isColor || isOut;
  const handX = FINGER_TARGET.x - FINGER_TIP.x * FINGER_SCALE; // aligned position; offset via transform
  const handY = FINGER_TARGET.y - FINGER_TIP.y * FINGER_SCALE;
  // All three lit variants stay mounted (preloaded); only the right one fades
  // in. Swapping href at result time painted the old sprite while the new one
  // decoded — that was the blue-then-purple flash.
  const litGreen  = isColor && isGreen && !dvc;
  const litPurple = isColor && isGreen && dvc;
  const litCyan   = isColor && isBlue;

  /* Cast member: pose + line */
  const pose =
    isGreen ? "magical" :
    isBlue ? "sad" :
    (phase === "standby" && !armed && !dormant) ? "one" :
    "welcome";
  const hiLine = lastName
    ? `Hi, ${lastName} Family! I'll be with you in just one second...`
    : "Hi there! I'll be with you in just one second...";
  const line =
    phase === "standby"
      ? (dormant ? MSG_DORMANT : asking ? MSG_DVC_ASK : armed ? MSG_TAP : hiLine)
      : phase === "welcome" || phase === "scan" || phase === "ready" ? (pointerHint ? MSG_POINTER : MSG_HERE_WE_GO)
      : phase === "prompt"
        ? (fingerDown || resultRef.current
            ? (longHold ? MSG_KEEP : MSG_DETECTED)
            : nudge ? MSG_NUDGE : MSG_FINGER)
      : isGreen ? (dvc ? MSG_DVC_HOME : MSG_GREEN)
      : isBlue ? MSG_BLUE : "";

    return (
      <div className="ixtv-scanwrap" onKeyDown={handleScannerKey} onBlur={handleScannerBlur} onFocusCapture={handleScannerFocus}>
      <style>{IXTV_STYLE}</style>
      {/* Controls bar — its own strip under the tab row; the art ring drops
          while the remote is up here. */}
      <div className="ixtv-ctlbar">
        <button className={`ix-dvc-pill ${dvc ? "on" : ""}`} ref={dvcPillRef} tabIndex={0} onClick={toggleDvc}>
          <span>DVC</span>
          <span className="ix-switch"><span className="ix-knob" /></span>
        </button>
                <button className="ix-back-btn" ref={backFloatRef} tabIndex={0} onClick={leaveScanner}>Back</button>
      </div>
      
            <div className="ix-scanner ixtv-sink" ref={sinkRef} tabIndex={0}>

      {/* Cast member, pinned left (scooted toward the reader on TV) */}
      <div className="ix-cast">
        <div className={`ix-speech ${isOut ? "ix-speech-out" : ""}`}>{line}</div>
        <div className="ix-cast-stack">
          <img className={`ix-cast-img ${pose === "welcome" ? "on" : ""}`} src={CAST_WELCOME_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "magical" ? "on" : ""}`} src={CAST_MAGICAL_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "sad" ? "on" : ""}`} src={CAST_SAD_SRC} alt="" draggable="false" />
          <img className={`ix-cast-img ${pose === "one" ? "on" : ""}`} src={CAST_ONE_SRC} alt="" draggable="false" />
        </div>
      </div>

      {/* Scanner art, shipped right */}
            <div className={`ix-stage-wrap ${fingerDown ? "ix-finger-down" : ""}`}>
        <img className="ix-img" src={BASE_SRC} alt="" draggable="false" />

        <svg className="ix-stage" viewBox="0 0 1280 800" aria-hidden="true">
          <defs>
            <clipPath id="ixtv-scanner-gap" clipPathUnits="userSpaceOnUse">
              <path clipRule="evenodd" d={SCANNER_GAP_CLIP} />
            </clipPath>
          </defs>

          {globeSweep ? (
            <g className="ix-ring-group">
              <GlobeLayers sweep lapMs={globeFastLap ? SCAN_LAP_MS : STANDBY_LAP_MS} />
            </g>
          ) : (
            <g className="ix-ring-group" style={globeStyle}>
              <GlobeLayers />
            </g>
          )}

          <g className={`ix-ring-group ${scannerFlash ? "ix-flashing" : ""}`} style={scannerStyle}>
            <ScannerLayers />
          </g>

          {/* Pulsing "press here" glow in the middle of the globe */}
          {showTapPulse && (
            <g className="ix-tap-pulse">
              <circle className="ix-tap-pulse-soft" cx={GLOBE.cx} cy={GLOBE.cy} r="92" />
              <circle className="ix-tap-pulse-core" cx={GLOBE.cx} cy={GLOBE.cy} r="34" />
            </g>
          )}

          {phase === "prompt" && (
            <g className="ix-arrow">
              <path className="ix-arrow-halo" style={{ stroke: dvc ? PURPLE : GREEN, opacity: 0.55 }} d={`M ${ARROW_POS.x - 30} ${ARROW_POS.y - 35} L ${ARROW_POS.x} ${ARROW_POS.y} L ${ARROW_POS.x + 30} ${ARROW_POS.y - 35}`} />
              <path className="ix-arrow-core" d={`M ${ARROW_POS.x - 30} ${ARROW_POS.y - 35} L ${ARROW_POS.x} ${ARROW_POS.y} L ${ARROW_POS.x + 30} ${ARROW_POS.y - 35}`} />
            </g>
          )}

          {showHand && (
            <g style={{ pointerEvents: "none", transform: `translate(${fingerOff}px, 0px)`, transition: "transform 140ms ease" }}>
              <image
                href={FINGER_SRC}
                x={handX} y={handY}
                width={FINGER_RENDER_W} height={FINGER_RENDER_H}
              />
              <image href={FINGER_GREEN_SRC} x={handX} y={handY} width={FINGER_RENDER_W} height={FINGER_RENDER_H}
                style={{ opacity: litGreen ? 1 : 0, transition: `opacity ${DISSOLVE_MS}ms ease` }} />
              <image href={FINGER_PURPLE_SRC} x={handX} y={handY} width={FINGER_RENDER_W} height={FINGER_RENDER_H}
                style={{ opacity: litPurple ? 1 : 0, transition: `opacity ${DISSOLVE_MS}ms ease` }} />
              <image href={FINGER_CYAN_SRC} x={handX} y={handY} width={FINGER_RENDER_W} height={FINGER_RENDER_H}
                style={{ opacity: litCyan ? 1 : 0, transition: `opacity ${DISSOLVE_MS}ms ease` }} />
            </g>
          )}

          <circle
            className="ix-hit"
            cx={GLOBE.cx} cy={GLOBE.cy} r={GLOBE.rx + 40}
            onPointerDown={handleReaderTap}
          />
          <rect
            className="ix-hit"
            x={SCANNER.cx - 150} y={SCANNER.cy - 80} width={300} height={190}
            onPointerDown={handleFingerDown}
            onPointerUp={handleFingerUp}
            onPointerCancel={handleFingerUp}
          />
                </svg>
      </div>

      {/* DVC ask — popup like the takeover modal (bubble buttons were
          unusable on remote). Keys stay on the sink; highlight is
          state-driven off askFocus, so left/right + OK work as before. */}
      {asking && (
        <div className="ixtv-modal-back">
          <div className="ixtv-modal">
            <h3>{MSG_DVC_ASK}</h3>
            <div className="ixtv-modal-btns">
              <button className={`ixtv-modal-btn ixtv-modal-yes ${askFocus === "yes" ? "ixtv-ask-on" : ""}`} onClick={() => chooseDvc(true)}>YES</button>
              <button className={`ixtv-modal-btn ixtv-modal-no ${askFocus === "no" ? "ixtv-ask-on" : ""}`} onClick={() => chooseDvc(false)}>NO</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
});

export default InteractTV;
